import { TranslateLoader } from '@ngx-translate/core';
import { combineLatest, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { catchError, filter, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { LocalStorageService } from '@cash/shared/local-storage';
import { LOCAL_STORAGE_PREFIX } from '../constants';
import { AuthenticationFacade } from '@cash/shared/authentication';

export interface ModulesResponse {
  module: string;
  version: number;
  dictionary: Record<string, string>;
}

@Injectable()
export class DictionaryTranslateLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private authenticationFacade: AuthenticationFacade
  ) {}

  getTranslation(lang: string): Observable<unknown> {
    const localDictionary = this.getDictionaryFromLocalStorage(lang) ?? [];
    const params = this.extractApiParameters(localDictionary);

    const serverDictionary = this.authenticationFacade.hasValidAccessToken$.pipe(
      filter((hasAccessToken) => hasAccessToken),
      take(1),
      switchMap(() => {
        return this.http
          .get<ModulesResponse[]>(`${environment.api.cash.i18n.root}/modules`, { params })
          .pipe(catchError(() => of([])));
      })
    );

    return combineLatest([of(localDictionary), serverDictionary.pipe(startWith([]))]).pipe(
      map(([localDictionary, serverResponse]) =>
        this.mergeLocalDictionaryWithResponse(serverResponse, localDictionary)
      ),
      tap((response) => this.updateDictionaryInLocalStorage(lang, response)),
      map((response) => this.extractTranslationsFromResponse(response))
    );
  }

  private mergeLocalDictionaryWithResponse(
    response: ModulesResponse[],
    localDictionary: ModulesResponse[]
  ) {
    if (response.length > 0) {
      return response.map((value) => {
        const foundDictionary = localDictionary.find((dic) => dic.module === value.module);
        const mergedDictionary = foundDictionary?.dictionary
          ? { ...foundDictionary.dictionary, ...value.dictionary }
          : value.dictionary;
        return {
          ...value,
          dictionary: mergedDictionary
        };
      });
    }

    return localDictionary;
  }

  private extractTranslationsFromResponse = (response: ModulesResponse[]): Record<string, string> =>
    response.reduce((map, obj) => {
      if (obj.dictionary && obj.module) map[obj.module] = obj.dictionary;
      return map;
    }, {});

  private updateDictionaryInLocalStorage = (lang: string, response: ModulesResponse[]) => {
    this.localStorageService.store(`${LOCAL_STORAGE_PREFIX}${lang}`, response);
  };

  private extractApiParameters = (dictionary: ModulesResponse[]): Record<string, string> =>
    dictionary.reduce((map, obj) => {
      if (obj.version && obj.module) map[obj.module] = obj.version;
      return map;
    }, {});

  private getDictionaryFromLocalStorage = (lang: string): ModulesResponse[] => {
    try {
      return this.localStorageService.get(`${LOCAL_STORAGE_PREFIX}${lang}`);
    } catch (e) {
      console.error(
        '[LocalStorageTranslateLoader] ! Failed to parse dictionary saved in localStorage, cleaning it for the next interation'
      );
      this.localStorageService.clear(`${LOCAL_STORAGE_PREFIX}${lang}`);
    }

    return [];
  };
}
